import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { ListingImageEdit } from "./ListingImageEdit";
import { Button } from "../ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import {
  BarterForSection,
  CategorySection,
  DescriptionSection,
  LocationSection,
  TitleSection,
} from "./FormSection";
import { EditButtons } from "./EditButtons";
import {
  getListingById,
  updateListing,
  updateListingImage,
} from "../../api/listingApi";
import { DeleteListing } from "./deleteListing";

export const EditListingForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // formData.location holds:
  //   { coordinates: [lng, lat], label, addressParts?, precision? } or null
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "",
    location: null,
    barterFor: [],
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [currentImageUrl, setCurrentImageUrl] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listingFound, setListingFound] = useState(true);
  const [submitError, setSubmitError] = useState("");

  // Track whether the user changed the location during this edit session
  const [locationDirty, setLocationDirty] = useState(false);

  useEffect(() => {
    const loadListing = async () => {
      try {
        setLoading(true);

        const listing = await getListingById(id);

        if (listing) {
          // listing.location is the new GeoJSON shape
          let locationData = null;
          if (
            listing.location &&
            Array.isArray(listing.location.coordinates) &&
            listing.location.coordinates.length === 2
          ) {
            locationData = {
              coordinates: listing.location.coordinates,
              label: listing.location.label,
              addressParts: listing.location.addressParts,
              precision: listing.location.precision,
            };
          }

          setFormData({
            title: listing.title || "",
            description: listing.description || "",
            category: listing.category || "",
            location: locationData,
            barterFor: listing.barterFor || [],
          });

          if (listing.image) {
            setCurrentImageUrl(listing.image);
            setImagePreview(listing.image);
          }
        } else {
          setListingFound(false);
        }
      } catch (error) {
        console.error("Error loading listing:", error);
        setListingFound(false);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadListing();
    }
  }, [id]);

  const handleChange = (field) => (eOrValue) => {
    const value = eOrValue?.target ? eOrValue.target.value : eOrValue;
    setFormData((prev) => ({ ...prev, [field]: value }));

    if (field === "location") {
      setLocationDirty(true);
    }

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleImageChange = (file) => {
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(currentImageUrl || null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.description.trim()) {
      newErrors.description = "Description is required";
    }

    if (!formData.category || (!formData.category._id && !formData.category)) {
      newErrors.category = "Category is required";
    }

    // Location must exist (either unchanged from load, or set by the picker)
    if (
      !formData.location ||
      !Array.isArray(formData.location.coordinates) ||
      formData.location.coordinates.length !== 2
    ) {
      newErrors.location = "Location is required";
    }

    if (!imageFile && !currentImageUrl) {
      newErrors.image = "Image is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const categoryId = formData.category._id || formData.category;
      const barterForIds = formData.barterFor.map((item) => item._id || item);

      const data = {
        title: formData.title,
        description: formData.description,
        category: categoryId,
        barterFor: barterForIds,
      };

      // Only send location if the user changed it during this session.
      // If not changed, the listing keeps its existing location.
      if (locationDirty && formData.location) {
        data.location = JSON.stringify({
          coordinates: formData.location.coordinates,
          precision: formData.location.precision || "geocoded",
        });
      }

      // Convert to FormData (the route uses upload.none() but expects multipart-style fields)
      const formDataPayload = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => formDataPayload.append(`${key}[]`, v));
        } else {
          formDataPayload.append(key, value);
        }
      });

      const result = await updateListing(id, formDataPayload);

      if (!result.success) {
        setSubmitError(result.message || "Failed to update listing");
        return;
      }

      navigate(-1, {
        state: { message: "Listing updated successfully!" },
      });
    } catch (error) {
      console.error("Error updating listing:", error);
      setSubmitError("An error occurred while updating the listing");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageSubmit = async () => {
    setSubmitError("");

    if (!imageFile) {
      setSubmitError("Please select an image to upload");
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await updateListingImage(id, imageFile);

      if (!result.success) {
        setSubmitError(result.message || "Failed to update image");
        return;
      }

      setSubmitError("");
      setCurrentImageUrl(result.listing.image);
      setImagePreview(result.listing.image);
      setImageFile(null);
      navigate(-1, {
        state: { message: "Listing updated successfully!" },
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      setSubmitError("An error occurred while uploading the image");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-igbanwo-mint" />
            <span className="ml-2 text-gray-600">Loading listing...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!listingFound) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Listing Not Found
          </h1>
          <p className="text-gray-600 mb-8">
            The listing you're trying to edit doesn't exist or you don't have
            permission to edit it.
          </p>
          <Button onClick={() => navigate("/browse")} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Listings
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 animate-fade-in">
      <TitleSection
        value={formData.title}
        onChange={handleChange("title")}
        error={errors.title}
      />

      <DescriptionSection
        value={formData.description}
        onChange={handleChange("description")}
        error={errors.description}
      />

      <CategorySection
        value={formData.category}
        onChange={handleChange("category")}
        error={errors.category}
      />

      <LocationSection
        value={formData.location}
        onChange={handleChange("location")}
        error={errors.location}
      />

      <BarterForSection
        value={formData.barterFor}
        onChange={handleChange("barterFor")}
        error={errors.barterFor}
      />

      <div className="space-y-2">
        <ListingImageEdit
          imagePreview={imagePreview}
          onImageChange={handleImageChange}
          onRemoveImage={removeImage}
          error={errors.image}
          isEdit={true}
          currentImageUrl={currentImageUrl}
        />

        <Button
          type="button"
          onClick={handleImageSubmit}
          disabled={!imageFile || isSubmitting}
          className="w-fit"
        >
          {isSubmitting ? "Uploading..." : "Update Image"}
        </Button>
      </div>

      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{submitError}</p>
        </div>
      )}

      <EditButtons
        loading={loading}
        handleCancel={handleCancel}
        isSubmitting={isSubmitting}
      />

      <DeleteListing listingId={id} />
    </form>
  );
};